// Tags domain logic — two vocabularies ('supplier' | 'brief'), open creation
// with duplicate guards, admin stewardship (edit/archive/delete/merge), and
// tag-based filtering. Pure helpers (normalize/match/fuzzy) are exported for
// the picker + unit testing; the data ops are thin supabase wrappers that also
// write the audit log. Fusion-internal only.
import { supabase } from '../supabaseClient';

export const TAG_MAX_LEN = 30;
// Tag-safe colour palette — deliberately disjoint from the five reserved
// semantic hues (green / red / amber / teal / purple) so a coloured tag
// chip can never impersonate a status pill or workflow signal.
//
// Expanded to 8 in 2026-06 (palette-expand-to-8 migration). The four
// new colours pair with the originals so the picker reads as four
// "families" the admin can use to group related tags:
//   blue    #5B8DEF   indigo  #7B74E0    (cool — Mon-Fri lanes)
//   rose    #E0689E   magenta #C45BC9    (pink — categories / themes)
//   slate   #9197A8   steel   #6FA0B0    (neutral — admin / utility)
//   sand    #C9A86A   stone   #B7A98F    (warm — partner / external)
//
// Watch-points (per tag-palette-8.html): Steel sits nearest reserved
// teal — lighter and bluer but worth eyeing next to a teal status
// pill. Sand / Stone sit nearest amber — muted/desaturated where
// amber is saturated orange, but don't let a Sand tag land right
// beside an amber warning. DB whitelist (CHECK constraint) enforces
// this list; expanding the palette requires updating BOTH this
// constant AND the migration.
export const TAG_DEFAULT_COLOUR = '#5B8DEF';
// EXACTLY the four hexes the DB CHECK whitelists — see
// db/migrations/2026-06-tags-safe-palette-whitelist.sql. The palette was
// briefly expanded to eight (indigo / magenta / steel / stone), but the
// whitelist migration kept only these four "safe" hues (the extra four sit
// too close to reserved status/pill colours), so offering the other four
// produced a tags_colour_check violation on create. Expanding the palette
// requires updating BOTH this constant AND the migration in the same commit.
export const TAG_COLOUR_PRESETS = [
  '#5B8DEF',  // blue
  '#E0689E',  // rose
  '#9197A8',  // slate
  '#C9A86A',  // sand
];
// Reserved-hue list — kept for documentation only. The DB CHECK
// constraint is now a WHITELIST of TAG_COLOUR_PRESETS (see
// db/migrations/2026-06-tags-safe-palette-whitelist.sql), so the
// "what is forbidden" question is answered implicitly as
// "everything except the four tag-safe hexes." This export was
// previously used by the AdminTags picker validation; that picker
// has been simplified to only offer the four preset swatches, so
// the runtime check is no longer needed.
//
// Kept here as a comment-list so future readers know which hexes
// would collide with status / brief-type pills if anyone considers
// expanding the tag palette later.
export const TAG_RESERVED_HUES = [
  '#92D400', // fusion-green — Awarded status / awardable gate
  '#E0290A', // fusion-red   — cancelled / warning
  '#E89A2E', // fusion-amber — PENDING RESPONSE / Due Soon / attention
  '#2E8FA1', // fusion-teal  — Awaiting Responses / info / Competitive brief-type
  '#009159', // deep-green   — Exclusive brief-type / eco / active-turn
  '#A07CE6', // legacy purple — old Exclusive pill (retired; kept reserved)
  '#A569C9', // legacy purple — old Exclusive pill (retired; kept reserved)
  '#B87333', // legacy copper — old Competitive pill (retired; kept reserved)
  '#D8915C', // legacy copper — old Competitive pill (retired; kept reserved)
];
// Legacy palette hexes that are colour-adjacent to the new tag-safe set
// (legacy blue, legacy pink) — not reserved, but flagged so the AdminTags
// picker can prefer the tag-safe slot when a remap is offered.
export const TAG_LEGACY_HUES = ['#4F93E0', '#E875B0', '#DADCCD'];

// Junction table + id column per vocabulary.
const JUNCTION = {
  supplier: { table: 'supplier_tags', idCol: 'supplier_id' },
  brief: { table: 'brief_tags', idCol: 'brief_id' },
};

// Trim ends + collapse any internal whitespace run to a single space. Run
// before every create/rename AND before every duplicate check so " Cat  ering "
// and "Cat ering" compare equal.
export function normalizeTagName(raw) {
  return String(raw == null ? '' : raw).replace(/\s+/g, ' ').trim();
}

const lc = (s) => normalizeTagName(s).toLowerCase();

// Classic Levenshtein, capped small — only used for the "did you mean" nudge.
export function levenshtein(a, b) {
  a = String(a); b = String(b);
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 0; i < a.length; i++) {
    const cur = [i + 1];
    for (let j = 0; j < b.length; j++) {
      const cost = a[i] === b[j] ? 0 : 1;
      cur[j + 1] = Math.min(cur[j] + 1, prev[j + 1] + 1, prev[j] + cost);
    }
    prev = cur;
  }
  return prev[b.length];
}

// Case-insensitive substring matches, prefix-first then alphabetical, ties by
// usage desc. Each result carries the match offset so the chip can highlight
// the matched portion without mutating the name. `usage` is an optional
// Map(tagId -> count).
export function matchTags(query, tags, usage) {
  const q = lc(query);
  if (!q) return [];
  const out = [];
  for (const t of tags) {
    const name = String(t.name || '');
    const idx = name.toLowerCase().indexOf(q);
    if (idx === -1) continue;
    out.push({ tag: t, start: idx, len: q.length, isPrefix: idx === 0 });
  }
  out.sort((a, b) => {
    if (a.isPrefix !== b.isPrefix) return a.isPrefix ? -1 : 1;
    const an = a.tag.name.toLowerCase();
    const bn = b.tag.name.toLowerCase();
    if (an !== bn) return an < bn ? -1 : 1;
    const au = usage ? (usage.get(a.tag.id) || 0) : 0;
    const bu = usage ? (usage.get(b.tag.id) || 0) : 0;
    return bu - au;
  });
  return out;
}

// Closest existing tag within edit distance 2 (and not an exact match).
// Used only when there are no substring matches.
export function nearMatch(query, tags) {
  const q = lc(query);
  if (q.length < 3) return null;
  let best = null;
  let bestDist = 3;
  for (const t of tags) {
    const d = levenshtein(q, t.name.toLowerCase());
    if (d > 0 && d < bestDist) { best = t; bestDist = d; }
  }
  return best;
}

// Picker state machine. `tags` should be the non-archived vocabulary.
//   idle      — nothing typed
//   duplicate — exact (case-insensitive) match exists -> creation blocked
//   match     — substring matches exist (or none, but creatable)
//   near      — no substring match but a fuzzy-close tag exists
export function pickerState(query, tags) {
  const q = lc(query);
  if (!q) return 'idle';
  if (tags.some(t => t.name.toLowerCase() === q)) return 'duplicate';
  if (matchTags(query, tags).length > 0) return 'match';
  if (nearMatch(query, tags)) return 'near';
  return 'match';
}

// ---------------------------------------------------------------------------
// Data ops
// ---------------------------------------------------------------------------

export async function writeTagAudit(action, payload = {}) {
  try {
    await supabase.from('tag_audit_log').insert([{ action, ...payload }]);
  } catch (e) {
    console.warn('Tag audit write failed', e);
  }
}

// All tags in a vocabulary. Archived included by default for the admin page;
// pass { includeArchived: false } for pickers/filters.
export async function fetchVocabulary(vocabulary, { includeArchived = true } = {}) {
  let q = supabase.from('tags')
    .select('*, creator:internal_users!created_by(full_name)')
    .eq('vocabulary', vocabulary)
    .order('name');
  if (!includeArchived) q = q.eq('archived', false);
  const { data, error } = await q;
  if (error) console.warn('fetchVocabulary failed', error);
  return data || [];
}

// Usage counts for a vocabulary -> Map(tagId -> count). Junctions are small
// enough to count client-side.
export async function usageCounts(vocabulary) {
  const { table } = JUNCTION[vocabulary];
  const { data } = await supabase.from(table).select('tag_id');
  const map = new Map();
  for (const row of data || []) map.set(row.tag_id, (map.get(row.tag_id) || 0) + 1);
  return map;
}

// Create a tag. Name is normalized; uniqueness is enforced by the DB unique
// index (case-insensitive per vocabulary) — a 23505 error surfaces as a
// friendly duplicate message. Returns { data, error }.
export async function createTag({ name, vocabulary, colour = TAG_DEFAULT_COLOUR, actorId }) {
  const clean = normalizeTagName(name);
  if (!clean) return { data: null, error: { message: 'Tag name is required' } };
  if (clean.length > TAG_MAX_LEN) return { data: null, error: { message: `Max ${TAG_MAX_LEN} characters` } };
  const { data, error } = await supabase.from('tags')
    .insert([{ name: clean, vocabulary, colour, created_by: actorId || null }])
    .select('*')
    .single();
  if (error) {
    if (error.code === '23505') return { data: null, error: { message: `"${clean}" already exists` } };
    return { data: null, error };
  }
  await writeTagAudit('create', { vocabulary, tag_id: data.id, tag_name: data.name, actor_id: actorId || null });
  return { data, error: null };
}

export async function updateTag(tag, { name, colour }, actorId) {
  const patch = { updated_at: new Date().toISOString() };
  if (name != null) {
    const clean = normalizeTagName(name);
    if (!clean) return { data: null, error: { message: 'Tag name is required' } };
    if (clean.length > TAG_MAX_LEN) return { data: null, error: { message: `Max ${TAG_MAX_LEN} characters` } };
    patch.name = clean;
  }
  if (colour != null) patch.colour = colour;
  const { data, error } = await supabase.from('tags').update(patch).eq('id', tag.id).select('*').single();
  if (error) {
    if (error.code === '23505') return { data: null, error: { message: `"${patch.name}" already exists` } };
    return { data: null, error };
  }
  await writeTagAudit('edit', { vocabulary: tag.vocabulary, tag_id: tag.id, tag_name: data.name, actor_id: actorId || null });
  return { data, error: null };
}

export async function setArchived(tag, archived, actorId) {
  const { data, error } = await supabase.from('tags')
    .update({ archived, archived_at: archived ? new Date().toISOString() : null })
    .eq('id', tag.id).select('*').single();
  if (!error) {
    await writeTagAudit(archived ? 'archive' : 'restore', {
      vocabulary: tag.vocabulary, tag_id: tag.id, tag_name: tag.name, actor_id: actorId || null,
    });
  }
  return { data, error };
}

// Delete a tag and remove it from every record (cascade). Records the
// affected count in the audit row. Only allowed on archived tags by the UI.
export async function deleteTag(tag, actorId) {
  const { table } = JUNCTION[tag.vocabulary];
  const { count } = await supabase.from(table).select('tag_id', { count: 'exact', head: true }).eq('tag_id', tag.id);
  const { error } = await supabase.from('tags').delete().eq('id', tag.id);
  if (!error) {
    await writeTagAudit('delete', {
      vocabulary: tag.vocabulary, tag_id: tag.id, tag_name: tag.name,
      affected_count: count || 0, actor_id: actorId || null,
    });
  }
  return { error, affected: count || 0 };
}

// Atomic merge via the merge_tags() rpc. Returns the affected record count.
export async function mergeTags(source, target, actorId) {
  const { data, error } = await supabase.rpc('merge_tags', { p_source: source.id, p_target: target.id });
  if (!error) {
    await writeTagAudit('merge', {
      vocabulary: source.vocabulary, tag_id: target.id,
      source_tag_name: source.name, target_tag_name: target.name,
      affected_count: data || 0, actor_id: actorId || null,
    });
  }
  return { error, affected: data || 0 };
}

// Apply / remove a tag on one record. Apply is idempotent (PK conflict ignored).
export async function applyTag(vocabulary, entityId, tagId, actorId) {
  const { table, idCol } = JUNCTION[vocabulary];
  return supabase.from(table)
    .upsert({ [idCol]: entityId, tag_id: tagId, tagged_by: actorId || null }, { onConflict: `${idCol},tag_id` });
}

export async function removeTag(vocabulary, entityId, tagId) {
  const { table, idCol } = JUNCTION[vocabulary];
  return supabase.from(table).delete().eq(idCol, entityId).eq('tag_id', tagId);
}

// Map(entityId -> [tag, ...]) for a set of records. One query over the
// junction joined to tags; grouped client-side. Empty input -> empty Map.
export async function fetchTagsFor(vocabulary, entityIds) {
  const map = new Map();
  if (!entityIds || entityIds.length === 0) return map;
  const { table, idCol } = JUNCTION[vocabulary];
  const { data, error } = await supabase.from(table)
    .select(`${idCol}, tags(*)`)
    .in(idCol, entityIds);
  if (error) { console.warn('fetchTagsFor failed', error); return map; }
  for (const row of data || []) {
    const tag = row.tags;
    if (!tag) continue;
    if (!map.has(row[idCol])) map.set(row[idCol], []);
    map.get(row[idCol]).push(tag);
  }
  for (const list of map.values()) list.sort((a, b) => a.name.localeCompare(b.name));
  return map;
}
